import { apiClient } from '@/lib/api';
import axiosInstance from '@/lib/api/axios-instance';
import type { StoreConnection, StorePushLog, PushRequest, ResultadoDeEnvio } from '../types';

/**
 * L7 ronda B (T10-5): una tanda de 500 precios espera hasta 30 s a la tienda, más la
 * validación en Prixia; 90 s cubre eso sin cortar un envío que la tienda está aplicando.
 */
const ESPERA_DE_UN_ENVIO_MS = 90_000;

export const storeConnectionsApi = {
  /** Listar todas las conexiones de tienda */
  getAll: async (): Promise<StoreConnection[]> => {
    return apiClient.get('/api/store-connections');
  },

  /** Crear una nueva conexión */
  create: async (
    data: Pick<StoreConnection, 'name' | 'storeUrl' | 'storeSecret'>
  ): Promise<StoreConnection> => {
    return apiClient.post('/api/store-connections', data);
  },

  /** Actualizar una conexión existente */
  update: async (
    id: string,
    data: Partial<
      Pick<StoreConnection, 'name' | 'storeUrl' | 'storeSecret' | 'isActive' | 'autoPublish'>
    >
  ): Promise<StoreConnection> => {
    return apiClient.put(`/api/store-connections/${id}`, data);
  },

  /** Eliminar una conexión */
  delete: async (id: string): Promise<void> => {
    return apiClient.delete(`/api/store-connections/${id}`);
  },

  /** Probar la conexión a una tienda */
  test: async (id: string): Promise<{ status: string }> => {
    return apiClient.post(`/api/store-connections/${id}/test`);
  },

  /** Enviar precios a una tienda (una tanda de hasta 500). */
  push: async (connectionId: string, data: PushRequest): Promise<ResultadoDeEnvio> => {
    return apiClient.post(`/api/store-connections/${connectionId}/push`, data, {
      timeout: ESPERA_DE_UN_ENVIO_MS,
    });
  },

  /** Obtener historial de pushes */
  getHistory: async (params?: {
    pushType?: string;
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<{ logs: StorePushLog[]; total: number }> => {
    // La lista viene en `data` y el total en `pagination`, al lado: `apiClient.get`
    // devuelve sólo `data`, y leer `.logs` de un arreglo dejaba la pantalla en blanco.
    const { data } = await axiosInstance.get('/api/store-connections/history', { params });
    const logs: StorePushLog[] = Array.isArray(data?.data) ? data.data : [];
    return { logs, total: data?.pagination?.total ?? logs.length };
  },

  /** Revertir un push */
  revert: async (logId: string): Promise<StorePushLog> => {
    return apiClient.post(`/api/store-connections/history/${logId}/revert`);
  },
};
